import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { planService } from '../../../../../application/PlanService';
import { Plan } from '../../../../../domain/plan/Plan';
import { PlanFlat } from '../../../../../domain/plan/PlanFlat';
import useErrorMiddleware from '../../../../../hooks/errorMiddleware/useErrorMiddleware';
import { useRetrieveOrganization } from '../../../../../hooks/organization/useRetrieveOrganization';
import { useRetrieveActiveProject } from '../../../../../hooks/project/useRetrieveActiveProject';
import { useRetrieveRightPanelInfo } from '../../../../../hooks/right-panel/useRetrieveRightPanelInfo';
import { useShowSnackbarMessage } from '../../../../../hooks/snackbarMessage/useShowSnackbarMessage';
import { useRetrieveUser } from '../../../../../hooks/user/useRetrieveUser';

type UseOptimizePlanProps = {
  plan: PlanFlat;
  keepCurrentAssignment?: boolean;
};

type UseOptimizePlanResponse = ServiceResponse<({ plan, keepCurrentAssignment }: UseOptimizePlanProps) => void, Plan>;

export const useOptimizePlan = (): UseOptimizePlanResponse => {
  const { sessionExpiredLogout } = useErrorMiddleware();
  const { showSnackbarMessage } = useShowSnackbarMessage();
  const { data: organization } = useRetrieveOrganization();
  const { data: loggedUser } = useRetrieveUser();
  const { data: activeProject } = useRetrieveActiveProject();
  const queryClient = useQueryClient();
  const { detailId, detailType } = useRetrieveRightPanelInfo();

  const { isLoading, isSuccess, isError, mutate } = useMutation(
    ({ plan, keepCurrentAssignment }: UseOptimizePlanProps) =>
      planService.optimize({
        plan,
        project: activeProject!,
        keepCurrentAssignment,
        isOwner: organization?.isOwner(loggedUser?.id),
      }),
    {
      onSuccess: (updatedPlan: Plan) => {
        queryClient.invalidateQueries([`planner_active_plan`]);
        queryClient.invalidateQueries([`planner_active_plan_drivers`]);
        queryClient.invalidateQueries([`planner_active_plan_stops`]);

        if (detailType === `stop` && detailId) {
          queryClient.invalidateQueries([`planner_stop`, detailId]);
        }

        // showSnackbarMessage({
        //   messageIntlId: `infoerror.optimizersucces`,
        //   type: `success`,
        //   autoHideDuration: 2000,
        // });
      },
      onError: (err: any) => {
        console.info(`Optimize Plan Error:`, err);

        // Timeout error (large optimizations)
        if (err?.code === `ERR_NETWORK`) {
          return;
        }

        if (err?.response?.status === 401) {
          sessionExpiredLogout();
          return;
        }

        if (err?.response?.status === 520) {
          showSnackbarMessage({
            messageIntlId: `infoerror.long_optimization`,
            type: `success`,
            autoHideDuration: 120000,
          });
          return;
        }

        switch (err?.response?.data?.messageId) {
          case `highway.subscription.error.expired_subscription`:
            showSnackbarMessage({
              messageIntlId: `infoerror.optimization_failed_invalid_subscription`,
              autoHideDuration: 6000,
            });
            return;
          case `highway.optimization.error.sync_optimization_already_progress`:
            showSnackbarMessage({
              messageIntlId: `infoerror.sync_optimization_already_progress`,
            });
            return;
          case `highway.optimization.error.no_result_found`:
            showSnackbarMessage({
              messageIntlId: `infoerror.no_result_found`,
            });
            return;
          default:
            showSnackbarMessage({
              messageIntlId: `infoerror.optimizererror`,
            });
        }
      },
    }
  );

  return {
    isLoading,
    error: isError,
    success: isSuccess,
    run: mutate,
  };
};
